import { DatePipe } from "@angular/common";
import { Component, computed, effect, inject, input, OnDestroy, OnInit, signal } from "@angular/core";
import { Router } from "@angular/router";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faSearch, faStar } from "@fortawesome/free-solid-svg-icons";
import { NgbPaginationModule } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";
import { map } from "rxjs";

import { SafeHtmlPipe, TranslateTokenPipe } from "../../core/pipes";
import { AdaaHelper, isDsV2 } from "../../core/utils";
import { Constants } from "../../shared/constants/constants";
import { BlockedCharsDirective } from "../../shared/directives/blocked-chars-directive";
import type {
  SearchCountAggregateType,
  SearchModelType,
  SearchResultItemType,
  SearchResultType,
  SearchTermTypes,
} from "../../shared/models";
import { FilesApiService, LanguageService, SearchApiService, SystemLayoutService } from "../../shared/services";
import { SearchProvider } from "./search.provider";
import {
  BLOCKED_SEARCH_CHARS,
  getActivityUrl,
  getKpiName,
  getKpiUrl,
  getProjectsUrl,
  getSearchCategories,
  getServiceName,
  hasBadWord,
  isProjectType,
  isSubIPTType,
  isSubKPIType,
} from "./search.utils";

@Component({
  selector: "adaa-search",
  imports: [
    TranslatePipe,
    FontAwesomeModule,
    NgbPaginationModule,
    SafeHtmlPipe,
    TranslateTokenPipe,
    DatePipe,
    BlockedCharsDirective,
  ],
  templateUrl: "./search.html",
  styleUrl: "./search.scss",
})
export class SearchComponent implements OnInit, OnDestroy {
  readonly languageService = inject(LanguageService);
  readonly systemLayoutService = inject(SystemLayoutService);
  private readonly _router = inject(Router);
  private readonly _searchProvider = inject(SearchProvider);
  private readonly _toastrService = inject(ToastrService);
  private readonly _filesApiService = inject(FilesApiService);
  private readonly _translateService = inject(TranslateService);
  private readonly _searchApiService = inject(SearchApiService);

  s = input<string | null>(null); //* reads from the query params ?s=
  t = input<"" | SearchTermTypes | null>(null); //* reads from the query params ?t=

  data = signal<SearchModelType | null>(null);
  searchResultList = computed(() => {
    const list = this.data()?.searchResultList ?? [];
    if (list.length === 0) return [];
    return list.map((item) => ({
      ...item,
      label: this.getLabel(item.type, {
        itemType: item.itemType,
        id: item.id,
        subType: item.subType,
        planId: item.planId,
      }),
    }));
  });

  isLoading = signal<boolean>(false);
  isParentActive = signal<boolean>(false);

  totalNumberOfItems = computed(() => {
    const list = this.data()?.countAggregate ?? [];
    const searchCount = this.data()?.searchCount;
    if (AdaaHelper.isDefinedAndNotEmpty(searchCount)) return searchCount;
    return list.reduce<number>((a, c: SearchCountAggregateType) => {
      if (!c.type || isSubKPIType(c.type) || isProjectType(c.type) || isSubIPTType(c.type)) return a;
      return a + c.count;
    }, 0);
  });

  currentPage = 1;
  offset = 0;
  activeIndex = signal<number>(0);

  readonly blockedChars = BLOCKED_SEARCH_CHARS;
  readonly limit = 10;
  readonly faStar = faStar;
  readonly faSearch = faSearch;
  readonly datetimeFormat = Constants.normalDate;
  readonly uaeTimezone = Constants.uaeTimezone;
  readonly getKpiName = getKpiName;
  readonly isSubKpiType = isSubKPIType;
  readonly isDefined = AdaaHelper.isDefined.bind(AdaaHelper);
  readonly searchCategories = getSearchCategories();
  readonly isActive = (t: string | null) => {
    const type = this.t();
    if (!type && t === "") return true;
    return t === type;
  };
  readonly getCount = (t: string) => {
    const category = this.data()?.countAggregate.find(({ type }) => type?.toLowerCase() === t?.toLowerCase());
    if (category) return category.count;
    return 0;
  };

  readonly #untilDestroy = AdaaHelper.untilDestroyed();
  readonly #doSearch = effect(() => {
    const searchTerm = this.s();
    const type = this.t();

    if (!searchTerm || searchTerm.length < 3) return;
    this._doSearch();

    if (type && (isSubKPIType(type) || isProjectType(type) || type.toLowerCase() === "kpi")) {
      this.isParentActive.set(true);
    }
  });

  public get resultsFound() {
    const total = this.offset + this.limit;
    let items: number;
    if (!this.t() || this.t() === "") items = this.totalNumberOfItems();
    else items = this.data()?.searchCount as number;
    if (total > items) return items;
    return total;
  }

  public ngOnInit() {
    this._refreshPage();
  }

  public ngOnDestroy() {
    this.systemLayoutService.searchTerm.set(null);
  }

  public onPageChange() {
    this.offset = this.limit * (this.currentPage - 1);
    this._doSearch();
  }

  public onClick(item: SearchResultType) {
    if (item.type === "formula") {
      return this._router.navigate(["/console", "formulas", item.id]);
    }
    if (item.type === "user") {
      return this._router.navigate(["/console", "users", item.id]);
    }
    if (item.type === "task") {
      return this._router.navigate(["/console", "delivery-module", item.id]);
    }
    if (item.type === "document" || item.type === "attachment") {
      return this._filesApiService.save(item.id, AdaaHelper.getItemValueByToken(item, "name"), true);
    }
    if (item.type === "service") {
      return this._router.navigate(getServiceName(item.itemType, item.id).url);
    }
    if (item.type === "government_direction") {
      return this._router.navigate(["/console", "directions", item.id]);
    }
    if (item.type === "sub_government_direction") {
      return this._router.navigate(["/console", "directions", item.parentId]);
    }
    if (item.type === "transformational_project") {
      return this._router.navigate(["/national-projects-dashboard", "projects", item.id]);
    }
    if (item.type === "transformational_milestone") {
      return this._router.navigate(["/national-projects-dashboard", "milestones", item.id]);
    }
    if (item.type === "annual_meeting_project") {
      return this._router.navigate(["/annual-meetings-projects-dashboard", "projects", item.id]);
    }
    if (item.type === "annual_meeting_milestone") {
      return this._router.navigate(["/annual-meetings-projects-dashboard", "milestones", item.id]);
    }
    if (item.type === "international_project") {
      return this._router.navigate(["/console", "international-projects", "projects", item.id]);
    }
    if (item.type === "international_milestone") {
      return this._router.navigate(["/console", "international-projects", "milestones", item.id]);
    }
    if (item.type === "regular_task") {
      return this._router.navigate(["/console", "project", "regular-task", item.id]);
    }
    if (item.type === "strategic_project") {
      return this._router.navigate(["/console", "project", "strategic-project", item.id]);
    }
    if (item.type === "transformational_project_new") {
      return this._router.navigate(["/console", "project", "m1", item.id]);
    }
    if (item.type === "operational_activities") {
      return this._router.navigate(["/console", "activity", "operational", item.id]);
    }
    if (item.type === "strategic_activities") {
      return this._router.navigate(["/console", "activity", "strategic", item.id]);
    }
    if (item.type === "transformational_project_milestone_new") {
      return this._router.navigate(["/console", "activity", "m1", item.id]);
    }

    switch (item.itemType) {
      case Constants.KPI_TYPE.NKPI:
      case Constants.KPI_TYPE.SKPI:
      case Constants.KPI_TYPE.EKPI:
      case Constants.KPI_TYPE.OPM:
      case Constants.KPI_TYPE.UAE:
      case Constants.KPI_TYPE.SRVKPI:
      case Constants.KPI_TYPE.MOKPI:
      case Constants.KPI_TYPE.MTKPI:
      case Constants.KPI_TYPE.NTKPI:
      case Constants.KPI_TYPE.DTKPI:
      case Constants.KPI_TYPE.IPTKPI:
      case Constants.KPI_TYPE.IMKPI: {
        return this._router.navigate(getKpiUrl(item.itemType, item.id));
      }
      case Constants.CONSTANT_ACTIVITYTYPE: {
        return this._router.navigate(getActivityUrl(item.id, item.subType));
      }
      case Constants.CONSTANT_OBJECTIVETYPE: {
        return this._searchProvider.objectivesRedirection(item.id, item.entityId);
      }
      case Constants.CONSTANT_INITIATIVE: {
        return this._router.navigate(getProjectsUrl(item.id, item.subType));
      }
      case Constants.CONSTANT_PILLARS_TYPE: {
        return this._router.navigate(["/console", "pillars", item.id]);
      }
      default:
        break;
    }

    return;
  }

  public searchButtonClicked(el: HTMLInputElement) {
    const searchTerm = this.systemLayoutService.searchTerm();
    if (!searchTerm || searchTerm.length < 3) {
      el.focus();
      this._toastrService.warning(this._translateService.instant("notification.warning.text_search_length"));
      return;
    }
    return this._router.navigate(["/console", "search"], { queryParams: { s: searchTerm } });
  }

  public autoUpdateSearchTerm($e: Event) {
    const target = $e.target as unknown;
    const value = (target as Record<string, string | null>).value;
    if (hasBadWord(value ?? "")) {
      this._toastrService.warning(this._translateService.instant("search.stop_words_message"));
      return;
    }
    this.systemLayoutService.searchTerm.set(value);
  }

  public performSearch($e: Event) {
    $e.preventDefault();
    this.autoUpdateSearchTerm($e);
    const searchTerm = this.systemLayoutService.searchTerm();
    if (!searchTerm || searchTerm.length < 3) {
      this._toastrService.warning(this._translateService.instant("notification.warning.text_search_length"));
      return;
    }
    return this._router.navigate(["/console", "search"], { queryParams: { s: searchTerm } });
  }

  public getName(data: SearchResultType) {
    let name = "";
    if (this.languageService.current() === "ae" && !this.isDefined(data.nameAE)) {
      name = data.nameEN;
    }
    if (this.languageService.current() === "ae" && this.isDefined(data.nameAE)) {
      name = data.nameAE;
    }
    if (this.languageService.current() === "en" && !this.isDefined(data.nameEN)) {
      name = data.nameAE;
    }
    if (this.languageService.current() === "en" && this.isDefined(data.nameEN)) {
      name = data.nameEN;
    }
    return name;
  }

  public getDescription(data: SearchResultType) {
    let name = "";
    if (this.languageService.current() === "ae" && !this.isDefined(data.descriptionAE)) {
      name = data.descriptionEN;
    }
    if (this.languageService.current() === "ae" && this.isDefined(data.descriptionAE)) {
      name = data.descriptionAE;
    }
    if (this.languageService.current() === "en" && !this.isDefined(data.descriptionEN)) {
      name = data.descriptionAE;
    }
    if (this.languageService.current() === "en" && this.isDefined(data.descriptionEN)) {
      name = data.descriptionEN;
    }
    return name;
  }

  public selectType($e: Event, type: string, index?: number) {
    $e.stopPropagation();
    if (type === "") {
      this.isParentActive.set(false);
      return this._router.navigate(["/console", "search"], {
        queryParams: { s: this.s() ?? "" },
        onSameUrlNavigation: "reload",
        preserveFragment: false,
      });
    }

    if (AdaaHelper.isDefinedAndNotEmpty(index)) this.activeIndex.set(index);
    if (isSubKPIType(type) || isProjectType(type) || isSubIPTType(type)) {
      this.isParentActive.set(true);
    } else {
      this.isParentActive.set(!this.isParentActive());
    }

    return this._router.navigate(["/console", "search"], {
      queryParams: { s: this.s() ?? "", t: type },
      onSameUrlNavigation: "reload",
      preserveFragment: false,
    });
  }

  public getLabel(
    type: Lowercase<SearchResultItemType>,
    data: { itemType?: number; subType?: number; id?: number; planId?: number }
  ) {
    if (type === "attachment" || type === "document") return "services.attachments";
    if (type === "government_direction") return "directions.government_directions";
    if (type === "sub_government_direction") return "directions.sub_government";
    if (type === "task") return "tasks.tasks";
    if (type === "tag") return "plusbutton.tags";
    if (type === "formula") return "formulas.formula_console_icon";
    if (type === "user") return "user.console";
    if (type === "pillars") return "entity.create.pillars";
    if (type === "service") return getServiceName(data.itemType as number, data.id as number).text;
    if (
      type === "kpi" ||
      type === "dtkpi" ||
      type === "ekpi" ||
      type === "mokpi" ||
      type === "iptkpi" ||
      type === "mtkpi" ||
      type === "nkpi" ||
      type === "ntkpi" ||
      type === "opm" ||
      type === "skpi" ||
      type === "srvkpi" ||
      type === "uae" ||
      type === "imkpi"
    ) {
      const coreLabel = this._translateService.instant("kpi.kpis");
      const label = this._translateService.instant(this.getKpiName(data.itemType as number));
      return `${coreLabel} - ${label}`;
    }

    if (type === "initiative") {
      const projectTitle = this._translateService.instant("initiatives.project");
      const categoryTitle = this._translateService.instant(
        data.subType === Constants.INITIATIVE_TYPES.REGULAR_TASK
          ? "initiatives.regular_task"
          : "initiatives.strategic_project"
      );
      return !isDsV2(data.planId) ? "breadcrumbs.initiative" : `${projectTitle} - ${categoryTitle}`;
    }

    if (type === "activity") {
      const projectTitle = this._translateService.instant("activities.activity");
      const categoryTitle = this._translateService.instant(
        data.subType === Constants.ACTIVITY_TYPES.OPERATIONAL_ACTIVITY
          ? "activities.operational"
          : "activities.strategic"
      );
      return `${projectTitle} ${isDsV2(data.planId) ? `- ${categoryTitle}` : ""}`;
    }

    if (type === "objective") {
      const projectTitle = this._translateService.instant("national_projects.projects");
      let categoryTitle = "";
      switch (data.subType) {
        case Constants.OBJECTIVE_TYPE.ENABLER_OBJECTIVE: {
          categoryTitle = "breadcrumbs.enablerobjective";
          break;
        }
        case Constants.OBJECTIVE_TYPE.MAIN_OBJECTIVE: {
          categoryTitle = "breadcrumbs.objectives.main";
          break;
        }
        case Constants.OBJECTIVE_TYPE.STRATEGIC_OBJECTIVE: {
          categoryTitle = "breadcrumbs.objectives.strategic";
          break;
        }
        case Constants.OBJECTIVE_TYPE.TRANSFORMATIONAL_OBJECTIVE: {
          categoryTitle = "breadcrumbs.objectives.transformational";
          break;
        }
        default:
          break;
      }
      return !isDsV2(data.planId)
        ? "objectives.objective"
        : `${projectTitle} - ${this._translateService.instant(categoryTitle)}`;
    }

    if (type === "transformational_project" || type === "transformational_milestone") {
      const coreLabel = this._translateService.instant("national_projects.console_title");
      const label = this._translateService.instant(
        type === "transformational_project" ? "national_strategies.initiatives" : "national_projects.milestones"
      );
      return `${coreLabel} - ${label}`;
    }

    if (type === "annual_meeting_project" || type === "annual_meeting_milestone") {
      const coreLabel = this._translateService.instant("annual_meetings.page_title");
      const label = this._translateService.instant(
        type === "annual_meeting_project" ? "national_strategies.initiatives" : "national_projects.milestones"
      );
      return `${coreLabel} - ${label}`;
    }

    if (
      type === "international_project" ||
      type === "international_milestone" ||
      data.itemType === Constants.KPI_TYPE.IPTKPI
    ) {
      const coreLabel = this._translateService.instant("international_projects.title");
      const label =
        data.itemType === Constants.KPI_TYPE.IPTKPI
          ? this._translateService.instant("international_projects.projects.ipts")
          : this._translateService.instant(
              type === "international_project"
                ? "international_projects.projects.single"
                : "international_projects.milestones.single"
            );
      return `${coreLabel} - ${label}`;
    }

    if (type === "strategic_project" || type === "regular_task" || type === "transformational_project_new") {
      const coreLabel = this._translateService.instant("national_projects.projects");
      switch (data.itemType) {
        case Constants.PROJECT_TYPES_DS3.STRATEGIC_PROJECT:
          return `${coreLabel} - ${this._translateService.instant("breadcrumbs.initiative_module.strategic_project_list")}`;
        case Constants.PROJECT_TYPES_DS3.REGULAR_TASK:
          return `${coreLabel} - ${this._translateService.instant("breadcrumbs.initiative_module.regular_task_list")}`;
        case Constants.PROJECT_TYPES_DS3.M1:
          return `${coreLabel} - ${this._translateService.instant("national_projects.project_names")}`;
        default:
          break;
      }
      const label = this._translateService.instant("strategic_projects.projects");
      return `${coreLabel} - ${label}`;
    }

    if (
      type === "operational_activities" ||
      type === "strategic_activities" ||
      type === "transformational_project_milestone_new"
    ) {
      const coreLabel = this._translateService.instant("activities.title");
      switch (data.itemType) {
        case Constants.ACTIVITY_TYPES_DS3.OPERATIONAL_ACTIVITY:
          return `${coreLabel} - ${this._translateService.instant("breadcrumbs.activity.operational_list")}`;
        case Constants.ACTIVITY_TYPES_DS3.STRATEGIC_ACTIVITY:
          return `${coreLabel} - ${this._translateService.instant("breadcrumbs.activity.strategic_list")}`;
        case Constants.ACTIVITY_TYPES_DS3.M1_MILESTONE:
          return `${coreLabel} - ${this._translateService.instant("national_projects.milestones")}`;
        default:
          break;
      }
    }

    return "";
  }

  private _doSearch() {
    const searchTerm = this.s();
    let type: string = this.t() ?? "";
    if (type === "MPROJECT") {
      type = "TRANSFORMATIONAL_PROJECT";
    }
    if (type === "AGM") {
      type = "ANNUAL_MEETING_PROJECT";
    }
    if (type === "INTProject") {
      type = "INTERNATIONAL_PROJECT";
    }
    if (type === "STRATEGIC_PROJECT") {
      type = "STRATEGIC_PROJECTS";
    }
    if (type === "REGULAR_TASK") {
      type = "REGULAR_TASKS";
    }
    if (type === "PROJECTACTIVITY") {
      type = "PROJECT_ACTIVITY";
    }
    if (type === "TRANSFORMATIONAL_PROJECT_MILESTONE_NEW") {
      type = "TRANSFORMATIONAL_PROJECTS_MILESTONES_NEW";
    }

    if (!searchTerm || searchTerm.length < 3) return;

    this.isLoading.set(true);
    this._searchApiService
      .doSearch({
        offset: this.offset,
        limit: this.limit,
        query: searchTerm.toLocaleLowerCase(),
        type: (type ?? "") as SearchResultItemType,
      })
      .pipe(map(({ responseData }) => responseData))
      .subscribe({
        next: (data) => {
          this.systemLayoutService.searchTerm.set(searchTerm || null);
          this.data.set(data);
        },
        error: () => {
          this.isLoading.set(false);
        },
        complete: () => {
          this.isLoading.set(false);
        },
      });
  }

  private _refreshPage() {
    this.systemLayoutService.hasActiveEntityChanged$.pipe(this.#untilDestroy()).subscribe({
      next: () => window.location.reload(),
    });

    this.systemLayoutService.hasCycleChanged$.pipe(this.#untilDestroy()).subscribe({
      next: () => window.location.reload(),
    });
  }
}
